import { supabase } from '../lib/supabase'
import { fetchAllByRange } from '../lib/supabaseFetchAll'
import type {
  CctClausula,
  CctClausulaConvencaoRow,
  CctClausulaInput,
  CctClausulasConvencaoFilters,
} from '../types/cctParametros'

function normalizeJoin<T>(value: T | T[] | null | undefined): T | null {
  if (value == null) return null
  if (Array.isArray(value)) return value[0] ?? null
  return value
}

function normalizeBuscaStatus(status: string | null | undefined): string {
  const bruto = (status || '').trim().toUpperCase()
  if (
    bruto === 'NÃO LOCALIZADA MTE' ||
    bruto === 'NAO LOCALIZADA MTE' ||
    bruto === 'NÃO LOCALIZADA' ||
    bruto === 'NAO LOCALIZADA'
  ) {
    return 'NÃO LOCALIZADA'
  }
  if (bruto === 'IMPORTADO SCGP' || bruto === 'IMPORTADO') {
    return 'IMPORTADO SCGP'
  }
  if (bruto === 'DISPONÍVEL PARA IMPORTAR' || bruto === 'DISPONIVEL PARA IMPORTAR') {
    return 'DISPONÍVEL PARA IMPORTAR'
  }
  return bruto
}

type ConvencaoResumo = {
  laboral: string | null
  patronal: string | null
  num_vidas: number | null
  convenios_qt: number | null
}

function uniqueConvencaoIds(
  rows: Array<{ cct_buscas: { cct_convencao_id: number | null } | null }>,
): number[] {
  return Array.from(
    new Set(
      rows
        .map((row) => row.cct_buscas?.cct_convencao_id)
        .filter((id): id is number => id != null),
    ),
  )
}

async function loadConvencoesResumo(ids: number[]): Promise<Map<number, ConvencaoResumo>> {
  const map = new Map<number, ConvencaoResumo>()
  const batchSize = 1000
  for (let i = 0; i < ids.length; i += batchSize) {
    const batchIds = ids.slice(i, i + batchSize)
    const { data, error } = await supabase
      .from('cct_convencoes')
      .select('id, cnpj_entidade_laboral, cnpj_entidade_patronal, num_vidas, convenios_qt')
      .in('id', batchIds)
    if (error) throw error
    for (const conv of data ?? []) {
      map.set(Number(conv.id), {
        laboral: (conv.cnpj_entidade_laboral as string | null) ?? null,
        patronal: (conv.cnpj_entidade_patronal as string | null) ?? null,
        num_vidas: conv.num_vidas != null ? Number(conv.num_vidas) : null,
        convenios_qt: conv.convenios_qt != null ? Number(conv.convenios_qt) : null,
      })
    }
  }
  return map
}

function matchCnpjFilter(value: string | null | undefined, query: string | undefined): boolean {
  if (!query?.trim()) return true
  const q = query.trim()
  const qDigits = q.replace(/\D/g, '')
  const v = value ?? ''
  if (qDigits.length > 0) {
    return v.replace(/\D/g, '').includes(qDigits)
  }
  return v.toLowerCase().includes(q.toLowerCase())
}

function matchVigenciaDateFilter(
  dateValue: string | null | undefined,
  filtroStr: string | undefined,
  operador: 'igual' | 'a_partir_de' | 'ate',
  mode: 'inicio' | 'fim',
): boolean {
  if (!filtroStr?.trim()) return true
  if (!dateValue) return false

  const data = new Date(dateValue)
  const filtro = filtroStr.trim()
  const isIgual = operador === 'igual'

  if (filtro.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const filtroInicio = new Date(`${filtro}T00:00:00`)
    const filtroFim = new Date(`${filtro}T23:59:59`)
    if (mode === 'inicio') {
      return isIgual
        ? data.getTime() >= filtroInicio.getTime() && data.getTime() <= filtroFim.getTime()
        : data.getTime() >= filtroInicio.getTime()
    }
    return isIgual
      ? data.getTime() >= filtroInicio.getTime() && data.getTime() <= filtroFim.getTime()
      : data.getTime() <= filtroFim.getTime()
  }

  if (filtro.match(/^\d{4}-\d{2}$/)) {
    const [ano, mes] = filtro.split('-')
    const anoItem = data.getFullYear()
    const mesItem = data.getMonth() + 1
    if (mode === 'inicio') {
      return isIgual
        ? anoItem === parseInt(ano, 10) && mesItem === parseInt(mes, 10)
        : anoItem > parseInt(ano, 10) || (anoItem === parseInt(ano, 10) && mesItem >= parseInt(mes, 10))
    }
    return isIgual
      ? anoItem === parseInt(ano, 10) && mesItem === parseInt(mes, 10)
      : anoItem < parseInt(ano, 10) || (anoItem === parseInt(ano, 10) && mesItem <= parseInt(mes, 10))
  }

  if (filtro.match(/^\d{4}$/)) {
    const anoItem = data.getFullYear()
    const anoFiltro = parseInt(filtro, 10)
    if (mode === 'inicio') {
      return isIgual ? anoItem === anoFiltro : anoItem >= anoFiltro
    }
    return isIgual ? anoItem === anoFiltro : anoItem <= anoFiltro
  }

  return true
}

export const cctParametrosService = {
  async listClausulas(): Promise<CctClausula[]> {
    const res = await fetchAllByRange<CctClausula>((from, to) =>
      supabase
        .from('cct_clausulas')
        .select('*')
        .order('id')
        .range(from, to),
    )

    if (res.error) throw new Error(res.error)
    return res.data
  },

  async listClausulasConvencao(
    filters?: CctClausulasConvencaoFilters,
  ): Promise<CctClausulaConvencaoRow[]> {
    type RawRow = {
      id: string
      cct_clausula_id: number
      cct_busca_id: number
      clausulas: number[] | null
      cct_clausulas: CctClausulaConvencaoRow['cct_clausulas'] | CctClausulaConvencaoRow['cct_clausulas'][] | null
      cct_buscas: CctClausulaConvencaoRow['cct_buscas'] | CctClausulaConvencaoRow['cct_buscas'][] | null
    }

    const res = await fetchAllByRange<RawRow>((from, to) => {
      let query = supabase
        .from('cct_clausulas_convencao')
        .select(`
          id,
          cct_clausula_id,
          cct_busca_id,
          clausulas,
          cct_clausulas ( id, nome, descricao ),
          cct_buscas ( id, codigo, status, cct_convencao_id, id_scgp, link_documento, link_download, vigencia_inicio, vigencia_fim, created_at )
        `)

      if (filters?.cctClausulaIds?.length) {
        query = query.in('cct_clausula_id', filters.cctClausulaIds)
      }

      return query.order('cct_busca_id', { ascending: false }).range(from, to)
    })

    if (res.error) throw new Error(res.error)

    let rows = res.data.map((row) => ({
      id: row.id,
      cct_clausula_id: row.cct_clausula_id,
      cct_busca_id: row.cct_busca_id,
      clausulas: row.clausulas ?? null,
      cct_clausulas: normalizeJoin(row.cct_clausulas),
      cct_buscas: normalizeJoin(row.cct_buscas),
    }))

    if (filters?.idScgp?.trim()) {
      const idScgpQuery = filters.idScgp.trim()
      rows = rows.filter((row) =>
        String(row.cct_buscas?.id_scgp ?? '').includes(idScgpQuery),
      )
    }

    if (filters?.numeroMte?.trim()) {
      const numeroMteQuery = filters.numeroMte.trim().toLowerCase()
      rows = rows.filter((row) =>
        (row.cct_buscas?.codigo ?? '').toLowerCase().includes(numeroMteQuery),
      )
    }

    let convencaoPorId = new Map<number, ConvencaoResumo>()

    if (filters?.cnpjEntidadePatronal?.trim() || filters?.cnpjEntidadeLaboral?.trim()) {
      convencaoPorId = await loadConvencoesResumo(uniqueConvencaoIds(rows))

      rows = rows.filter((row) => {
        const convencaoId = row.cct_buscas?.cct_convencao_id
        const cnpjs = convencaoId != null ? convencaoPorId.get(convencaoId) : undefined
        return (
          matchCnpjFilter(cnpjs?.patronal, filters.cnpjEntidadePatronal)
          && matchCnpjFilter(cnpjs?.laboral, filters.cnpjEntidadeLaboral)
        )
      })
    }

    if (filters?.statuses?.length) {
      const selectedStatuses = new Set(filters.statuses.map((status) => status.trim()))
      rows = rows.filter((row) =>
        selectedStatuses.has(normalizeBuscaStatus(row.cct_buscas?.status)),
      )
    }

    if (filters?.vigenciaInicio?.trim()) {
      rows = rows.filter((row) =>
        matchVigenciaDateFilter(
          row.cct_buscas?.vigencia_inicio,
          filters.vigenciaInicio,
          filters.operadorVigenciaInicio ?? 'a_partir_de',
          'inicio',
        ),
      )
    }

    if (filters?.vigenciaFim?.trim()) {
      rows = rows.filter((row) =>
        matchVigenciaDateFilter(
          row.cct_buscas?.vigencia_fim,
          filters.vigenciaFim,
          filters.operadorVigenciaFim ?? 'ate',
          'fim',
        ),
      )
    }

    if (convencaoPorId.size === 0) {
      convencaoPorId = await loadConvencoesResumo(uniqueConvencaoIds(rows))
    }

    return rows.map((row) => {
      const convencaoId = row.cct_buscas?.cct_convencao_id
      const convencao = convencaoId != null ? convencaoPorId.get(convencaoId) : undefined
      return {
        ...row,
        num_vidas: convencao?.num_vidas ?? null,
        convenios_qt: convencao?.convenios_qt ?? null,
      }
    })
  },

  async upsertClausula(input: CctClausulaInput): Promise<void> {
    const payload = {
      nome: input.nome.trim(),
      descricao: input.descricao.trim() || null,
    }

    if (input.id) {
      const { error } = await supabase
        .from('cct_clausulas')
        .update(payload)
        .eq('id', input.id)
      if (error) throw error
    } else {
      const { error } = await supabase
        .from('cct_clausulas')
        .insert(payload)
      if (error) throw error
    }
  },

  async deleteClausula(id: number): Promise<void> {
    const { error } = await supabase
      .from('cct_clausulas')
      .delete()
      .eq('id', id)

    if (error) throw error
  },
}
